import { BubbleMenu, Editor } from "@tiptap/react";
import { useRef } from "react";
import Menu from "./Menu";

type Props = {
    editor: Editor;
    showColorPicker?: boolean;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    contentBlockId?: string;
};

/**
 * Resolve where tippy should append the bubble's formatting popper.
 *
 * Preference order:
 *  1. #rich-text-bubble-portal — a leaf host React never renders into, mounted
 *     high in the editor tree. Descendant of #inertia-app (so React 18 event
 *     delegation reaches the menu's onClick), but not the createRoot container
 *     itself (so reconciliation can't strip tippy's appended node), and above
 *     the card-option z-[1000] stacking contexts.
 *  2. #inertia-app — the React root, used where the host isn't mounted
 *     (e-learning, design studio). Events still work; positioning is unchanged
 *     from before the portal host existed.
 *  3. document.body — last resort if neither element is present.
 */
export function resolveBubbleAppendTo(): HTMLElement {
    return (
        document.getElementById("rich-text-bubble-portal") ??
        document.getElementById("inertia-app") ??
        document.body
    );
}

export function RichTextTooltipMenu({
    editor,
    showColorPicker,
    isOpen = false,
    setIsOpen,
    contentBlockId,
}: Props) {
    const openRef = useRef<boolean>(false);
    openRef.current = isOpen;

    return (
        <BubbleMenu
            editor={editor}
            tippyOptions={{
                duration: 100,
                maxWidth: 412,
                onHidden: () => {
                    setIsOpen?.(false);
                },
                // NB: followCursor is incompatible with interactive — with it
                // on, the popper tracks the pointer so a mousedown→mouseup→click
                // never completes on a menu button (the click event is lost and
                // no formatting applies). The menu must be statically placed to
                // be clickable.
                interactive: true,
                // Append to the dedicated bubble portal host, NOT document.body
                // or #inertia-app directly — see resolveBubbleAppendTo.
                appendTo: resolveBubbleAppendTo,
            }}
            className="pointer-events-auto relative z-[9999] flex items-center justify-center bg-white"
        >
            <Menu
                editor={editor}
                showColorPicker={showColorPicker}
                contentBlockId={contentBlockId}
                // Always inside a BubbleMenu — the bubble is already scoped to
                // this editor's selection, so don't gate on the active content
                // block (card-option editors never become active).
                skipActiveContentBlockGuard
            />
        </BubbleMenu>
    );
}
